import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { parse, differenceInCalendarDays } from 'date-fns'

// --- New design-system primitives ---
import { FormShell, FormGrid, FormField, FormFooter } from '../../../../../../generic/ui/FormLayout'
import { AccentButton } from '../../../../../../generic/ui/Button'
import IconTextField from '../../../../../../generic/ui/Input/IconTextField'
import GrowerSelector from '../../../../../../generic/ui/Selectors/GrowerSelector'
import ProductSelector from '../../../../../../generic/ui/Selectors/ProductSelector'
import SizeSelector from '../../../../../../generic/ui/Selectors/SizeSelector'
import PackageSelector from '../../../../../../generic/ui/Selectors/PackageSelector'
import PalletTypeSelector from '../../../../../../generic/ui/Selectors/PalletTypeSelector'
import QualitySelector from '../../../../../../generic/ui/Selectors/QualitySelector'
import HashIcon from '../../../../../../generic/icons/IconLib/HashIcon'
import ScaleIcon from '../../../../../../generic/icons/IconLib/ScaleIcon'
import CoinIcon from '../../../../../../generic/icons/IconLib/CoinIcon'

// --- Existing pieces (logic intentionally untouched) ---
import ProductImage from '../../../queries/TeudotDetails/Components/ProductImage'
import TypeCalcSelect from './generic/TypeCalcSelect'
import BarcodeScannerPanel from './BarcodeScannerPanel'
import useBarcodeScanner from '../hooks/useBarcodeScanner'
import { IMarketPalletDetails, IUpdateProduct } from '../../../../../../modules/API/Market/models/marketRequests'
import { IPalletTypeItem } from '../../../../../../modules/API/Defaults/models/pallets'
import { IGrowerItem } from '../../../../../../modules/API/Defaults/models/growers'
import { IPackagesItem } from '../../../../../../modules/API/Defaults/models/packages'
import { IProductsWithVarietyItem } from '../../../../../../modules/API/Defaults/models/compliteProducts'
import { IQualityItem } from '../../../../../../modules/API/Defaults/models/quality'
import { ISizeItem } from '../../../../../../modules/API/Products/models/size'
import { Gathering } from '../../../../../../modules/API/Gathering/gathering'
import { IResponseSrikaNode, ISrikaItem } from '../../../../../../modules/API/Gathering/models/srika'
import { handleNotifications } from '../../../../../../generic/utils/handleNotifications'
import { RootState } from '../../../../../../../store/reducers/rootReducer'
import selectBrodMigdal from '../../../../../../generic/utils/selectBrodMigdal'

type Props = {
    data: IMarketPalletDetails
    updateProduct: (data: IUpdateProduct) => void
    pallets: IPalletTypeItem[]
    growers: IGrowerItem[]
    isWorker: boolean
    packageList: IPackagesItem[]
    dataProductsWithVariety: IProductsWithVarietyItem[]
    getSizes: (productID: number) => void
    sizeData: ISizeItem[]
    qualityList: IQualityItem[]
}

const ProductModal = ({
    data, updateProduct, pallets, growers,
    isWorker, packageList,
    dataProductsWithVariety, getSizes, sizeData,
    qualityList,
}: Props) => {
    console.log('data', data)
    const [t] = useTranslation('teudot')
    const {
        input,
        setInput,
        barcodeList,
        scanBarcode,
    } = useBarcodeScanner(true)

    const withoutPalletID = useMemo(() => {
        return pallets.find((item) => item.Real_Pallete === 0)?.Pallete_Type || 0
    }, [pallets])

    const defaults = useSelector((state: RootState) => state.defaults.defaults)
    const isDisableTakeGoodsIfNotEnoughRest = selectBrodMigdal(defaults, 379, 'Logical_Value')
    const isNeedToCHeckProductWithoutSrika = selectBrodMigdal(defaults, 664, 'Logical_Value')

    const refQty = useRef<number>(data.Quantity)
    const [salePrice, setSalePrice] = useState<string>(data.SalePrice.toFixed(2))
    const [quantity, setQuantity] = useState<number>(data.Quantity)
    const [quantityPallets, setQuantityPallets] = useState<number>(data.Quantity_Pallets)
    const [packageID, setPackageID] = useState<number>(data.PackID)
    const [weightBrutto, setWeightBrutto] = useState<string>(data.WeightBrutto.toFixed(1))
    const [calcType, setCalcType] = useState<number>(data.Type_SaleCalculation)
    const [palletID, setPalletID] = useState<number>(data.Pallet_Type)
    const [growerID, setGrowerID] = useState<number>(data.GrowerID)
    const [isNeedtoCalcPallet, setIsNeedtoCalcPallet] = useState<boolean>(true)
    const [srika, setSrika] = useState<IResponseSrikaNode | null>(null)
    const [referenceDeliveryEntry, setReferenceDeliveryEntry] = useState<number>(data.Reference_Delivery_Entry || 0)
    const isDisableByEntry = data.EntryNum !== 0
    const [tareWeight, setTareWeight] = useState<number>(() =>
        pallets?.find(p => p.Pallete_Type === data.Pallet_Type)?.Tare_Weight || 0
    )
    const [sameBarcode, setSameBarcode] = useState<boolean>(isNeedToCHeckProductWithoutSrika ? false : true)

    const [selectedProduct, setSelectedProduct] = useState<IProductsWithVarietyItem | null>(
        () =>
            data.ProductID !== undefined && data.VarietyID !== undefined 
                ? dataProductsWithVariety.find(
                      (p) => p.ProductID === data.ProductID && p.VarietyID === data.VarietyID,
                  ) ?? null
                : null,
    )
    const [selectedSize, setSelectedSize] = useState<number>(0)
    const sizeInitRef = useRef<boolean>(false)
    const [selectedQualityID, setSelectedQualityID] = useState<number>(0)
    const qualityInitRef = useRef<boolean>(false)

    const isDisableEdit = data.InvoiceSale !== 0

    const handleCheckEntryDataByProduct = useCallback((item: ISrikaItem) => {
        const currentProduct = `${data.ProductDesc.trim()} ${data.VarietyDesc.trim()} ${data.SizeID.trim()}`
        const srikaProduct = `${item.ProductDesc.trim()} ${item.VarietyDesc.trim()} ${item.SizeDesc.trim()}`
        return currentProduct === srikaProduct
    }, [data])

    const isDisableEditWorker = useMemo(() => {
        if (!isWorker) return false
        return !sameBarcode
    }, [isWorker, sameBarcode])

    const palletDict = pallets.reduce((acc: Record<number, IPalletTypeItem>, curr) => {
        acc[curr.Pallete_Type] = curr
        return acc
    }, {})

    const isDisabledSave = useMemo(() => {
        if (isDisableTakeGoodsIfNotEnoughRest && srika) {
            const deltaQty = quantity - refQty.current
            const isNotEnough = deltaQty > 0 && srika.entryLine[0].BalanceAmount < deltaQty
            if (isNotEnough) {
                handleNotifications('אין מספיק כמות במלאי', { type: 'warning' })
            }
            return isNotEnough
        }
        return isDisableEdit
    }, [srika, isDisableTakeGoodsIfNotEnoughRest, isDisableEdit, quantity])
 
    const hasTeuda = data.Delivery_Num !== 0
    const isDeliveryPassed =
        hasTeuda &&
        !!data.Delivery_Date &&
        differenceInCalendarDays(new Date(), parse(data.Delivery_Date, 'yyyyMMdd', new Date())) >= 1
    const isDisableProductSize =
        isDisableEdit || isDisableByEntry || isDisableEditWorker || isDeliveryPassed
    // A real product must come from props (ProductID + VarietyID). When they're absent
    // (not sent by the API) or 0, treat it as "no product" and hide the product/size selector.
    const hasProductFromProps = Boolean(data.ProductID) 

    useEffect(() => {
        getSizes(selectedProduct?.ProductID || 0)
    }, [selectedProduct, getSizes])

    useEffect(() => {
        if (sizeInitRef.current || !sizeData.length) return
        const match = sizeData.find((s) => s.Description.trim() === data.SizeID?.trim())
        if (match) setSelectedSize(match.Code)
        sizeInitRef.current = true
    }, [sizeData, data.SizeID])

    useEffect(() => {
        if (qualityInitRef.current || !qualityList.length) return
        const match = qualityList.find((q) => q.QualityDesc.trim() === data.QualityDesc?.trim())
        if (match) setSelectedQualityID(match.QualityID)
        qualityInitRef.current = true
    }, [qualityList, data.QualityDesc])

    const handleSelectProduct = (item: IProductsWithVarietyItem | null) => {
        setSelectedProduct(item)
        handleTypeCalc(item?.CalculationMethod || 0)
        setSelectedSize(0)
    }

    const handleUpdateProduct = () => {
        if (isDisableEdit) {
            handleNotifications('לא ניתן לערוך מוצר עם חשבונית מכירה', { type: 'warning' })
        }
        const product: IUpdateProduct = {
            BuyPrice: +salePrice,
            Quantity: quantity,
            Quantity_Pallets: quantityPallets,
            Remarks: data.Remarks,
            SalePrice: +salePrice,
            ProductID: selectedProduct?.ProductID,
            VarietyID: selectedProduct?.VarietyID,
            SizeID: sizeData.find((s) => s.Code === selectedSize)?.Description.trim() || data.SizeID,
            isIncludePallet: isNeedtoCalcPallet,
            WeightBrutto: isNeedtoCalcPallet ? +weightBrutto : +weightBrutto + tareWeight,
            Type_SaleCalculation: calcType,
            Type_BuyCalculation: calcType,
            Pallet_Type: palletID,
            GrowerID: growerID,
            PackID: packageID,
            Reference_Delivery_Entry: referenceDeliveryEntry,
            Quality: selectedQualityID,
        }
        updateProduct(product)
    }

    const handleWeight = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        if (/^\d*\.?\d*$/.test(value)) {
            setWeightBrutto(value)
        }
    }

    const handleSelectPackage = (id: number) => {
        setPackageID(id)
        const item = packageList.find(item => item.PackID === id)
        if (item?.Pack_Type === 'מ') {
            setQuantityPallets(0)
            setPalletID(withoutPalletID)
        }
    }

    const handleQuantity = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = Number(e.target.value)
        setQuantity(Math.round(val))
    }

    const handleReferenceDeliveryEntry = (e: string) => {
        setReferenceDeliveryEntry(Number(e))
    }

    const handlePrice = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        if (/^\d*\.?\d*$/.test(value)) {
            setSalePrice(value)
        }
    }

    const handleTypeCalc = (e: number) => {
        setCalcType(e)
    }

    const handlePallet = (e: number) => {
        const delta = Math.round(palletDict[e].Tare_Weight - palletDict[palletID].Tare_Weight)
        setTareWeight(Math.round(palletDict[e].Tare_Weight))
        setWeightBrutto(prev => (Number(prev) + delta).toFixed(1))
        if (e === withoutPalletID) {
            setQuantityPallets(0)
        } else if (palletID === withoutPalletID) {
            setQuantityPallets(1)
        }
        setPalletID(e)
    }

    useEffect(() => {
        if (data.EntryNum === 0) return
        ;(async () => {
            try {
                const res = await Gathering.getKnisaByBarcode(`${data.EntryNum}-${data.EntryNumRow}`)
                setSrika(res)
            } catch (err) {
                handleNotifications('שגיאה בקבלת נתונים', { type: 'error' })
            }
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleQuantityPallets = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = Number(e.target.value)
        setQuantityPallets(Math.round(val))
    }

    const handleScanSuccess = (item: ISrikaItem) => {
        if (isNeedToCHeckProductWithoutSrika) {
            const isMatch = handleCheckEntryDataByProduct(item)
            if (!isMatch) {
                handleNotifications('קוד זה לא תואם למוצר זה', { type: 'warning' })
                return
            }
        }
        setSameBarcode(true)
    }

    const handleScanBarcode = async (barcode?: string) => {
        const code = barcode ?? input
        const validationString = `${data.EntryNum}-${data.EntryNumRow}`
        if (data.EntryNum !== 0 && code !== validationString) {
            handleNotifications('קוד זה לא תואם למוצר זה', { type: 'warning' })
            return
        }
        try {
            await scanBarcode(handleScanSuccess, barcode)
        } catch (error) {
            handleNotifications('שגיאה בקבלת נתונים', { type: 'error' })
        }
    }

    const handleSelectBarcode = (item: ISrikaItem) => {
        const isMatch = handleCheckEntryDataByProduct(item)
        if (data.EntryNum !== 0 && !isMatch) {
            handleNotifications('קוד זה לא תואם למוצר זה', { type: 'warning' })
            setSameBarcode(false)
            return
        }
        handleScanSuccess(item)
        setSameBarcode(true)
    }

    const handleCalculateAvarageWeight = () => {
        if (!srika || !quantity) return
        const avgWeight = srika.entryLine[0].AvgWeightPerPack
        setWeightBrutto((avgWeight * quantity).toFixed(1))
    }

    const showBarcodePanel = (data.EntryNum !== 0 || isNeedToCHeckProductWithoutSrika) && isWorker
    const showWorkerOverlay = isDisableEditWorker && (data.EntryNum !== 0 || isNeedToCHeckProductWithoutSrika)

    const growerValue = useMemo(
        () => growers.find(g => g.GrowerID === growerID) ?? null,
        [growers, growerID],
    )
    const palletValue = useMemo(
        () => pallets.find(p => p.Pallete_Type === palletID) ?? null,
        [pallets, palletID],
    )
    const packageValue = useMemo(
        () => packageList.find(p => p.PackID === packageID) ?? null,
        [packageList, packageID],
    )
    const productValue = useMemo(
        () =>
            dataProductsWithVariety.find(
                p =>
                    p.ProductID === selectedProduct?.ProductID &&
                    p.VarietyID === selectedProduct?.VarietyID,
            ) ?? null,
        [dataProductsWithVariety, selectedProduct],
    )
    const sizeValue = useMemo(
        () => sizeData.find(s => s.Code === selectedSize) ?? null,
        [sizeData, selectedSize],
    )
    const qualityValue = useMemo(
        () => qualityList.find(q => q.QualityID === selectedQualityID) ?? null,
        [qualityList, selectedQualityID],
    )
    console.log('calcType', dataProductsWithVariety.find(p => p.CalculationMethod ===  1 ))
    return (
        <FormShell>
            <ImageSlot>
                <ProductImage id={data.GeneralProductID} />
            </ImageSlot>

            {showBarcodePanel && (
                <BarcodeScannerPanel
                    input={input}
                    setInput={setInput}
                    onScan={handleScanBarcode}
                    onSelectFromList={handleSelectBarcode}
                    barcodeList={barcodeList?.entryLine}
                    autoFocus
                    disabled={false}
                />
            )}

            <FormGridWrapper>
                {showWorkerOverlay && (
                    <Overlay>
                        <OverlayText>הקוד שנספר לא תואם למוצר זה</OverlayText>
                    </Overlay>
                )}

                <FormGrid>
                    {hasProductFromProps && (
                        <PairField>
                            <FormField label={t('create_product_modal_product')}>
                                <ProductSelector
                                    products={dataProductsWithVariety}
                                    value={productValue}
                                    onSelect={handleSelectProduct}
                                    disabled={isDisableProductSize}
                                />
                            </FormField>
                            {selectedProduct && (
                                <FormField label={t('create_product_modal_size')}>
                                    <SizeSelector
                                        sizes={sizeData || []}
                                        value={sizeValue}
                                        onSelect={(s) => setSelectedSize(s ? s.Code : 0)}
                                        disabled={isDisableProductSize}
                                    />
                                </FormField>
                            )}
                        </PairField>
                    )}

                    {hasProductFromProps && (
                        <FormField label='איכות' fullWidth>
                            <QualitySelector
                                qualities={qualityList}
                                value={qualityValue}
                                onSelect={(q) => setSelectedQualityID(q ? q.QualityID : 0)}
                                disabled={isDisableProductSize}
                            />
                        </FormField>
                    )}

                    <FormField label={t('create_product_modal_grower')} fullWidth required={!growerID}>
                        <GrowerSelector
                            growers={growers}
                            value={growerValue}
                            onSelect={(g) => setGrowerID(g?.GrowerID || 0)}
                            disabled={isDisableEdit || isDisableByEntry}
                        />
                    </FormField>

                    <PairField>
                        <FormField label={t('create_product_modal_pallet')}>
                            <PalletTypeSelector
                                palletTypes={pallets}
                                value={palletValue}
                                onSelect={(p) => handlePallet(p?.Pallete_Type || 0)}
                                disabled={isDisableEdit}
                            />
                        </FormField>
                        <FormField label='כמות משטחים'>
                            <IconTextField
                                type='number'
                                inputMode='numeric'
                                leadingIcon={<HashIcon />}
                                value={quantityPallets}
                                onChange={handleQuantityPallets}
                                disabled={isDisableEdit}
                                onWheel={(e) => (e.currentTarget as HTMLInputElement).blur()}
                            />
                        </FormField>
                    </PairField>

                    <FormField label={t('create_product_modal_calcMethod')} fullWidth>
                        <TypeCalcSelect
                            defaultType={calcType}
                            handleSelect={handleTypeCalc}
                            disabled={isDisableEdit || isDisableByEntry}
                        />
                    </FormField>

                    <PairField>
                        <FormField label={t('create_product_modal_grower_package')}>
                            <PackageSelector
                                packages={packageList}
                                value={packageValue}
                                onSelect={(p) => handleSelectPackage(p?.PackID || 0)}
                                disabled={isDisableEdit || isDisableByEntry}
                            />
                        </FormField>
                        <FormField label={t('create_product_modal_grower_qty')}>
                            <IconTextField
                                type='number'
                                inputMode='numeric'
                                leadingIcon={<HashIcon />}
                                value={quantity}
                                onChange={handleQuantity}
                                disabled={isDisableEdit}
                                onWheel={(e) => (e.currentTarget as HTMLInputElement).blur()}
                            />
                        </FormField>
                    </PairField>

                    <PairField>
                        <FormField label={t('create_product_modal_weight')}>
                            <IconTextField
                                type='text'
                                inputMode='decimal'
                                leadingIcon={<ScaleIcon />}
                                endAdornment={
                                    srika ? (
                                        <AvgWeightBtn
                                            type='button'
                                            onClick={handleCalculateAvarageWeight}
                                            aria-label='avg-weight'
                                        >
                                            <ScaleIcon />
                                        </AvgWeightBtn>
                                    ) : undefined
                                }
                                value={weightBrutto}
                                onChange={handleWeight}
                                disabled={isDisableEdit || isDisableEditWorker}
                            />
                        </FormField>
                        {!isWorker && (
                            <FormField label={t('create_product_modal_price')}>
                                <IconTextField
                                    type='text'
                                    inputMode='decimal'
                                    leadingIcon={<CoinIcon />}
                                    value={salePrice}
                                    onChange={handlePrice}
                                    disabled={isDisableEdit || isWorker}
                                />
                            </FormField>
                        )}
                    </PairField>

                    {tareWeight > 0 && (
                        <FormField fullWidth>
                            <CheckRow
                                type='button'
                                onClick={() => setIsNeedtoCalcPallet(!isNeedtoCalcPallet)}
                                aria-pressed={isNeedtoCalcPallet}
                            >
                                <CheckBoxBox $checked={isNeedtoCalcPallet} aria-hidden>
                                    {isNeedtoCalcPallet && <CheckGlyph />}
                                </CheckBoxBox>
                                <CheckLabel>{t('create_product_modal_includeTareWeight')}</CheckLabel>
                            </CheckRow>
                        </FormField>
                    )}

                    <FormField label='אסמכתה' fullWidth>
                        <IconTextField
                            type='number'
                            inputMode='numeric'
                            leadingIcon={<HashIcon />}
                            value={referenceDeliveryEntry}
                            onChange={(e) => handleReferenceDeliveryEntry(e.target.value)}
                            placeholder='אסמכתה..'
                            disabled={isDisableEdit || isDisableByEntry}
                            onWheel={(e) => (e.currentTarget as HTMLInputElement).blur()}
                        />
                    </FormField>
                </FormGrid>
            </FormGridWrapper>

            {data.InvoiceSale === 0 && (
                <FormFooter>
                    <AccentButton
                        type='button'
                        onClick={handleUpdateProduct}
                        disabled={isDisabledSave}
                        fullWidth={false}
                    >
                        {t('create_product_modal_button_save')}
                    </AccentButton>
                </FormFooter>
            )}
        </FormShell>
    )
}

export default ProductModal

// ---------- Styled (themed) ----------

const ImageSlot = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    & > img {
        width: 4rem;
        height: 4rem;
        object-fit: contain;
        background: transparent;
    }
`

const FormGridWrapper = styled.div`
    position: relative;
    width: 100%;
`

const Overlay = styled.div`
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.2);
    z-index: 10;
    border-radius: ${({ theme }) => theme.radii.lg};
    display: flex;
    align-items: center;
    justify-content: center;
`

const OverlayText = styled.div`
    padding: 0.875rem 1.25rem;
    border-radius: ${({ theme }) => theme.radii.md};
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.ink};
    box-shadow: ${({ theme }) => theme.shadows.card};
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.md};
    direction: rtl;
`

const PairField = styled.div`
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 1rem 1.25rem;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        gap: 0.625rem 0.5rem;
    }

    & > *:only-child {
        grid-column: 1 / -1;
    }
`

const AvgWeightBtn = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border: 0;
    background: transparent;
    color: ${({ theme }) => theme.colors.ink2};
    cursor: pointer;
    border-radius: ${({ theme }) => theme.radii.sm};
    transition: background ${({ theme }) => theme.motion.fast} ease;

    svg {
        width: 1rem;
        height: 1rem;
    }

    &:hover {
        background: ${({ theme }) => theme.colors.accentSoft};
        color: ${({ theme }) => theme.colors.ink};
    }
`

const CheckGlyph = () => (
    <svg
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth={3}
        strokeLinecap='round'
        strokeLinejoin='round'
        aria-hidden
    >
        <polyline points='20 6 9 17 4 12' />
    </svg>
)

const CheckRow = styled.button`
    appearance: none;
    width: 100%;
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.5rem 0.625rem;
    border: 1px solid ${({ theme }) => theme.colors.line};
    background: ${({ theme }) => theme.colors.surface};
    border-radius: ${({ theme }) => theme.radii.md};
    cursor: pointer;
    text-align: start;
    direction: rtl;
    transition: all ${({ theme }) => theme.motion.fast} ease;

    &:hover:not(:disabled) {
        border-color: ${({ theme }) => theme.colors.accent};
        background: ${({ theme }) => theme.colors.accentSoft};
    }

    &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }
`

const CheckBoxBox = styled.span<{ $checked?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
    border-radius: ${({ theme }) => theme.radii.sm};
    border: 1px solid
        ${({ theme, $checked }) => ($checked ? theme.colors.accent : theme.colors.inputBorder)};
    background: ${({ theme, $checked }) =>
        $checked ? theme.colors.accent : theme.colors.surface};
    color: ${({ theme }) => theme.colors.ink};
    transition: all ${({ theme }) => theme.motion.fast} ease;

    svg {
        width: 0.875rem;
        height: 0.875rem;
    }
`

const CheckLabel = styled.span`
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.md};
    color: ${({ theme }) => theme.colors.ink};
`
